import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { X, ChevronDown } from 'lucide-react';

const BottomSheet = ({ isOpen, onClose, title, children, height = '90%', allowOverflow = false }) => {
  const dragControls = useDragControls();
  const containerRef = useRef(null);

  // Lock body scroll when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Premium Backdrop with gradient */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gradient-to-t from-black/80 via-black/60 to-black/40 backdrop-blur-md z-50"
            onClick={onClose}
          />

          {/* Bottom Sheet */}
          <motion.div
            ref={containerRef}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 300
            }}
            drag="y"
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(event, info) => {
              if (info.offset.y > 100) {
                onClose();
              }
            }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-b from-[#0f1f35] to-[#0c1a2d] rounded-t-[2rem] shadow-[0_-20px_60px_rgba(0,0,0,0.5)] border-t border-white/10"
            style={{ maxHeight: height, minHeight: '70vh' }}
          >
            {/* Premium Drag Handle */}
            <div 
              className="p-4 cursor-grab active:cursor-grabbing bg-gradient-to-b from-white/[0.03] to-transparent"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-16 h-1.5 bg-gradient-to-r from-emerald-400/40 via-emerald-400/60 to-emerald-400/40 rounded-full mx-auto mb-4 shadow-[0_0_20px_rgba(16,185,129,0.3)]" />
              
              {/* Premium Header */}
              <div className="flex items-center justify-between px-2">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-blue-300">{title}</h3>
                </div>
                <button
                  onClick={onClose}
                  className="p-2.5 bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-200 backdrop-blur-sm border border-white/10 hover:border-white/20"
                >
                  <X size={20} className="text-white/70 hover:text-white" />
                </button>
              </div>
            </div>

            {/* Premium Content Container */}
            <div className="relative">
              {/* Subtle top fade effect */}
              <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-[#0f1f35] to-transparent z-10 pointer-events-none" />
              
              {/* Content */}
              <div className={`${allowOverflow ? 'overflow-visible' : 'overflow-y-auto'} max-h-[calc(90vh-100px)] overscroll-contain px-1`}>
                <div className="pb-20">
                  {children}
                </div>
              </div>
              
              {/* Subtle bottom fade effect */}
              <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-[#0c1a2d] to-transparent pointer-events-none" />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BottomSheet;